import { Extension } from '@tiptap/core';
import { EditorState, Transaction } from '@tiptap/pm/state';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';

export interface IndentOptions {
  types: string[];
  step: number; // px per indent level
  max: number; // max indent levels
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

/**
 * Minimal block indentation: stores an `indent` level (0..max) on the configured block types and
 * renders it as a left margin. Indents every block in the current selection. Kept in-repo rather
 * than pulling a third-party indent package.
 */
export const Indent = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return { types: ['paragraph', 'heading'], step: 24, max: 8 };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: element => {
              const ml = parseInt((element as HTMLElement).style.marginLeft, 10);
              return ml ? Math.round(ml / this.options.step) : 0;
            },
            renderHTML: attributes => {
              if (!attributes.indent) return {};
              return { style: `margin-left: ${attributes.indent * this.options.step}px` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    const { types, max } = this.options;
    const applyIndent =
      (delta: number) =>
      ({ state, dispatch }: { state: EditorState; dispatch?: (tr: Transaction) => void }) => {
        const { tr, selection } = state;
        const { from, to } = selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node: ProseMirrorNode, pos: number) => {
          if (!types.includes(node.type.name)) return;
          const current = node.attrs.indent || 0;
          const next = Math.min(max, Math.max(0, current + delta));
          if (next !== current) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      };

    return {
      indent: () => applyIndent(1),
      outdent: () => applyIndent(-1),
    };
  },
});

export default Indent;